import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { useLanguage } from '@/contexts/AppContext';
import type { Gender } from '@/lib/api/types';
import { ProfileData } from '../../hooks/useProfileState';
import { profileTranslations } from './profileTranslations';

interface GenderSelectorProps {
  isEditing: boolean;
  profileData: ProfileData;
  setProfileData: React.Dispatch<React.SetStateAction<ProfileData>>;
}

const GENDER_OPTIONS: ReadonlyArray<{ value: Gender; labelKey: 'genderMale' | 'genderFemale' | 'genderOther' | 'genderPreferNotToSay' }> = [
  { value: 'male', labelKey: 'genderMale' },
  { value: 'female', labelKey: 'genderFemale' },
  { value: 'other', labelKey: 'genderOther' },
  { value: 'prefer_not_to_say', labelKey: 'genderPreferNotToSay' },
];

const PLACEHOLDER_VALUE = '__none__';

/**
 * 고객 프로필 성별 선택 컴포넌트.
 *
 * @CODE:CUSTOMER-PROFILE-275 | SPEC: menu#275 (admin#1060 Phase 1 정합)
 *
 * - admin#1060 Phase 1 BE 의 Gender enum 4 case 와 1:1 매핑
 * - LFPDPPP 정합 (Other / PreferNotToSay 옵션 제공)
 * - empty string ('') 은 미지정 — useProfileState 가 BE 전송 시 null 로 변환
 */
export function GenderSelector({
  isEditing,
  profileData,
  setProfileData,
}: GenderSelectorProps) {
  const { locale } = useLanguage();
  const t = profileTranslations[locale];

  const matchedOption = profileData.gender !== ''
    ? GENDER_OPTIONS.find((opt) => opt.value === profileData.gender)
    : undefined;
  const currentLabel = matchedOption ? t[matchedOption.labelKey] : '';

  return (
    <div className="space-y-[min(2.5vw,0.625rem)]">
      <label className="text-[min(3.5vw,0.875rem)] font-medium text-muted-foreground block">
        {t.gender}
      </label>
      {isEditing ? (
        <Select
          value={profileData.gender || PLACEHOLDER_VALUE}
          onValueChange={(value) => setProfileData({ ...profileData, gender: value as Gender })}
        >
          <SelectTrigger className="h-[min(12vw,3rem)] bg-background border-2 border-border rounded-[5px] px-[min(4vw,1rem)] focus:border-primary focus:ring-2 focus:ring-primary/20 w-full">
            <SelectValue placeholder={t.selectGender} />
          </SelectTrigger>
          <SelectContent>
            {GENDER_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {t[option.labelKey]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : (
        <div className="h-[min(12vw,3rem)] flex items-center px-[min(4vw,1rem)] bg-muted rounded-[5px]">
          <p className="text-[min(4vw,1rem)] text-foreground">
            {currentLabel}
          </p>
        </div>
      )}
    </div>
  );
}
